import React, { useEffect, useMemo, useRef, useState } from "react";
import { Typography } from "@mui/material";
import {
  AccessTime,
  Close,
  DonutLarge,
  Insights,
  NotificationsActive,
  QueryStats,
  ReceiptLong,
  TaskAlt,
  Timeline,
  TrendingUp,
} from "@mui/icons-material";
import styles from "./ActivityPanel.module.css";

const STORAGE_KEY = "posInvoiceHistory";

const readHistory = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const saveHistory = (records) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
};

const formatDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const formatDateTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { date: "", time: "" };
  return {
    date: date.toLocaleDateString(),
    time: date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
  };
};

const PANEL_ITEMS = [
  { key: "overview", label: "Overview", icon: <TrendingUp /> },
  { key: "monthly", label: "Sales Chart", icon: <Timeline /> },
  { key: "hourly", label: "Peak Hours", icon: <AccessTime /> },
  { key: "alerts", label: "Alerts", icon: <NotificationsActive /> },
  { key: "payments", label: "Payment Mix", icon: <QueryStats /> },
  { key: "trends", label: "POS Trends", icon: <Insights /> },
  { key: "targets", label: "Targets", icon: <DonutLarge /> },
  { key: "invoices", label: "History Invoices", icon: <ReceiptLong /> },
  { key: "tasks", label: "Follow-ups", icon: <TaskAlt /> },
];

const ActivityPanel = () => {
  const [history, setHistory] = useState(() => readHistory());
  const [activePanel, setActivePanel] = useState("overview");
  const [panelOpen, setPanelOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const onInvoiceCompleted = (event) => {
      const payload = event?.detail;
      if (!payload?.invoiceId) return;
      setHistory((prev) => {
        const next = [payload, ...prev].slice(0, 60);
        saveHistory(next);
        return next;
      });
    };
    window.addEventListener("pos:invoice-completed", onInvoiceCompleted);
    return () => window.removeEventListener("pos:invoice-completed", onInvoiceCompleted);
  }, []);

  const summary = useMemo(() => {
    const todayKey = new Date().toDateString();
    const todayRecords = history.filter(
      (record) => new Date(record.timestamp).toDateString() === todayKey
    );
    const todaySales = todayRecords.reduce((sum, row) => sum + (Number(row.total) || 0), 0);
    const avgSale = todayRecords.length ? todaySales / todayRecords.length : 0;
    const paymentCounts = todayRecords.reduce(
      (acc, row) => {
        const key = row.paymentMethod === "Card" ? "card" : "cash";
        acc[key] += 1;
        return acc;
      },
      { cash: 0, card: 0 }
    );
    const last7Days = Array.from({ length: 7 }).map((_, index) => {
      const date = new Date();
      date.setDate(date.getDate() - (6 - index));
      const key = date.toDateString();
      const dayTotal = history
        .filter((row) => new Date(row.timestamp).toDateString() === key)
        .reduce((sum, row) => sum + (Number(row.total) || 0), 0);
      return { label: date.toLocaleDateString([], { weekday: "short" }), total: dayTotal };
    });
    const maxDay = Math.max(1, ...last7Days.map((row) => row.total));
    return { todaySales, avgSale, todayInvoices: todayRecords.length, paymentCounts, last7Days, maxDay };
  }, [history]);

  const alerts = useMemo(() => {
    const lowAmountInvoices = history.filter((row) => Number(row.total) < 5).length;
    const cardShare = summary.todayInvoices
      ? Math.round((summary.paymentCounts.card / summary.todayInvoices) * 100)
      : 0;
    return [
      `${summary.todayInvoices} invoices processed today`,
      `${lowAmountInvoices} micro-transactions under $5`,
      `Card usage today at ${cardShare}%`,
    ];
  }, [history, summary]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const handleItemClick = (key) => {
    setActivePanel(key);
    setPanelOpen(true);
  };

  return (
    <aside ref={containerRef} className={`${styles.panel} ${styles.collapsed}`}>
      <div className={styles.collapsedIconWrap}>
        {PANEL_ITEMS.map((item) => (
          <button
            key={item.key}
            className={`${styles.menuItem} ${item.key === activePanel ? styles.selected : ""}`}
            title={item.label}
            onClick={() => handleItemClick(item.key)}
          >
            <span className={styles.menuIconBox}>{item.icon}</span>
          </button>
        ))}
      </div>

      {panelOpen && <div className={styles.backdrop} onClick={() => setPanelOpen(false)} />}

      <div className={`${styles.flyout} ${panelOpen ? styles.flyoutOpen : ""}`}>
        <div className={styles.header}>
          <div>
            <Typography className={styles.title}>
              {PANEL_ITEMS.find((item) => item.key === activePanel)?.label || "POS Activity"}
            </Typography>
            <Typography className={styles.subTitle}>POS activity insights</Typography>
          </div>
          <button className={styles.closeFlyout} onClick={() => setPanelOpen(false)} aria-label="Close panel">
            <Close fontSize="small" />
          </button>
        </div>

        <div className={styles.flyoutBody}>
          {activePanel === "overview" && (
            <>
              <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                  <Typography className={styles.statLabel}>Today Sales</Typography>
                  <Typography className={styles.statValue}>$ {summary.todaySales.toFixed(2)}</Typography>
                </div>
                <div className={styles.statCard}>
                  <Typography className={styles.statLabel}>Today Invoices</Typography>
                  <Typography className={styles.statValue}>{summary.todayInvoices}</Typography>
                </div>
                <div className={styles.statCard}>
                  <Typography className={styles.statLabel}>Avg Sale</Typography>
                  <Typography className={styles.statValue}>$ {summary.avgSale.toFixed(2)}</Typography>
                </div>
              </div>
              <div className={styles.section}>
                <Typography className={styles.sectionTitle}>Recent Invoices</Typography>
                <div className={styles.historyList}>
                  {history.slice(0, 8).map((row) => (
                    <div className={styles.historyItem} key={`${row.invoiceId}-${row.timestamp}`}>
                      <div>
                        <Typography className={styles.invoiceNo}>#INV-{String(row.invoiceId).padStart(6, "0")}</Typography>
                        <Typography className={styles.invoiceMeta}>{formatDate(row.timestamp)} | {row.paymentMethod}</Typography>
                      </div>
                      <Typography className={styles.invoiceAmount}>$ {(Number(row.total) || 0).toFixed(2)}</Typography>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {(activePanel === "monthly" || activePanel === "trends") && (
            <div className={styles.section}>
              <Typography className={styles.sectionTitle}>Sales Chart</Typography>
              <div className={styles.chartGridWide}>
                {summary.last7Days.map((day) => (
                  <div className={styles.chartItem} key={day.label}>
                    <div
                      className={styles.chartBar}
                      style={{ height: `${(day.total / summary.maxDay) * 100}%` }}
                      title={`$ ${day.total.toFixed(2)}`}
                    />
                    <span className={styles.chartLabel}>{day.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activePanel === "invoices" && (
            <div className={styles.section}>
              <Typography className={styles.sectionTitle}>History Invoices</Typography>
              <div className={styles.invoiceTableWrap}>
                <table className={styles.invoiceTable}>
                  <thead>
                    <tr>
                      <th>Invoice #</th>
                      <th>Amount</th>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Payment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.length === 0 ? (
                      <tr><td colSpan={5} className={styles.emptyCell}>No completed invoices yet</td></tr>
                    ) : (
                      history.slice(0, 30).map((row) => {
                        const { date, time } = formatDateTime(row.timestamp);
                        return (
                          <tr key={`${row.invoiceId}-${row.timestamp}`}>
                            <td>#INV-{String(row.invoiceId).padStart(6, "0")}</td>
                            <td>$ {(Number(row.total) || 0).toFixed(2)}</td>
                            <td>{date}</td>
                            <td>{time}</td>
                            <td>{row.paymentMethod}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activePanel === "alerts" && (
            <div className={styles.section}>
              <Typography className={styles.sectionTitle}>Alerts</Typography>
              <div className={styles.alertList}>{alerts.map((row) => <div className={styles.alertItem} key={row}>{row}</div>)}</div>
            </div>
          )}

          {activePanel === "payments" && (
            <div className={styles.section}>
              <Typography className={styles.sectionTitle}>Payment Mix (Today)</Typography>
              <div className={styles.paymentRow}><span>Cash</span><strong>{summary.paymentCounts.cash}</strong></div>
              <div className={styles.paymentRow}><span>Card</span><strong>{summary.paymentCounts.card}</strong></div>
            </div>
          )}

          {activePanel === "hourly" && (
            <div className={styles.section}>
              <Typography className={styles.sectionTitle}>Peak Hours</Typography>
              <div className={styles.paymentRow}><span>09:00 - 11:00</span><strong>High</strong></div>
              <div className={styles.paymentRow}><span>12:00 - 14:00</span><strong>Medium</strong></div>
              <div className={styles.paymentRow}><span>17:00 - 20:00</span><strong>High</strong></div>
            </div>
          )}

          {activePanel === "targets" && (
            <div className={styles.section}>
              <Typography className={styles.sectionTitle}>Monthly Targets</Typography>
              <div className={styles.paymentRow}><span>Revenue Goal</span><strong>$ 8,000</strong></div>
              <div className={styles.paymentRow}><span>Current Pace</span><strong>$ {summary.todaySales.toFixed(2)}</strong></div>
              <div className={styles.paymentRow}><span>Invoices Goal</span><strong>220</strong></div>
            </div>
          )}

          {activePanel === "tasks" && (
            <div className={styles.section}>
              <Typography className={styles.sectionTitle}>POS Follow-ups</Typography>
              <div className={styles.alertList}>
                <div className={styles.alertItem}>Verify low stock alerts before shift close</div>
                <div className={styles.alertItem}>Review cancelled invoices</div>
                <div className={styles.alertItem}>Export daily sales summary</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};

export default ActivityPanel;
